/**
 * BackHomeButton — Floating navigation pill shown on all pages
 * (except the entrance page and the 3D viewer).
 *
 * Shows "← Back" if the user has browser history, otherwise "🏠 Home".
 * Provides an escape hatch so users are never stuck on any page.
 */
import { useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import './BackHomeButton.css'

/** Exact pages where the button should NOT appear */
const HIDDEN_PATHS = ['/', '/login', '/browse', '/map', '/profile', '/shops', '/orders', '/trips', '/scan', '/location-picker', '/intro', '/messages']
/** Prefix patterns — hide on any path starting with these (pages with own back button) */
const HIDDEN_PREFIXES = ['/shop/', '/admin', '/checkout/', '/dashboard/', '/chat/', '/tracking/', '/trip/', '/shops/', '/orders/', '/claim/', '/reviews/']

export function BackHomeButton() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('common')

  // All hooks must be called unconditionally before any early return
  const hasHistory = window.history.length > 2

  const handleClick = useCallback(() => {
    if (hasHistory) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }, [hasHistory, navigate])

  // Hide on pages that have their own back/home navigation
  const path = location.pathname
  if (HIDDEN_PATHS.includes(path) || HIDDEN_PREFIXES.some(p => path.startsWith(p))) {
    return null
  }

  return (
    <button className="back-home-btn" onClick={handleClick}>
      {hasHistory ? (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>{t('goBack')}</span>
        </>
      ) : (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <span>{t('backToHome')}</span>
        </>
      )}
    </button>
  )
}
